import React, { useState, useEffect } from 'react';
import { ClipboardList, FileImage, MessageSquare, FileText, ExternalLink, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { requestsService } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RequestsPanel = ({ requests, onUpdate, focusRequestId, onFocusHandled }) => {
  const [updating, setUpdating] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  useEffect(() => {
    if (!focusRequestId) return;
    setSearchTerm(''); setStatusFilter('all'); setTypeFilter('all'); setDateFilter('all');
    setExpandedId(focusRequestId); setHighlightId(focusRequestId);
    const t = setTimeout(() => {
      document.getElementById(`request-${focusRequestId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onFocusHandled?.();
    }, 150);
    return () => clearTimeout(t);
  }, [focusRequestId, onFocusHandled]);

  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [highlightId]);

  const handleUpdateStatus = async (requestId, newStatus) => {
    setUpdating(requestId);
    try {
      const res = await requestsService.updateRequest(requestId, { status: newStatus });
      const data = res.data;
      if (data.email_sent) {
        toast.success(`Solicitud ${newStatus === 'approved' ? 'aprobada' : 'rechazada'} - Email enviado a ${data.email_to}`);
      } else {
        toast.success(`Solicitud ${newStatus === 'approved' ? 'aprobada' : 'rechazada'}`);
      }
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar solicitud');
    } finally {
      setUpdating(null);
    }
  };

  const filteredRequests = requests.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (typeFilter !== 'all' && r.request_type !== typeFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!r.debtor_name?.toLowerCase().includes(term) && !r.debtor_dni?.includes(term)) return false;
    }
    if (dateFilter !== 'all') {
      const now = new Date();
      const created = new Date(r.created_at);
      const diffDays = (now - created) / (1000 * 60 * 60 * 24);
      if (dateFilter === 'today' && diffDays > 1) return false;
      if (dateFilter === 'week' && diffDays > 7) return false;
      if (dateFilter === 'month' && diffDays > 30) return false;
    }
    return true;
  });

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4">
        <Input
          placeholder="Buscar por nombre o DNI..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-64"
          data-testid="requests-search"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="approved">Aprobados</SelectItem>
            <SelectItem value="rejected">Rechazados</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="certificate">Certificados</SelectItem>
            <SelectItem value="payment_report">Pagos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Fecha" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="today">Hoy</SelectItem>
            <SelectItem value="week">Esta semana</SelectItem>
            <SelectItem value="month">Este mes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredRequests.length === 0 ? (
        <Card className="p-12 text-center bg-white">
          <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No hay solicitudes{statusFilter !== 'all' ? ` con estado "${statusFilter}"` : ''}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => {
            const isExpanded = expandedId === request.id;
            const hasDetails = request.request_type === 'payment_report' && (
              request.proof_file_url || request.comments || (request.acta_numbers?.length > 0) || request.ai_analysis
            );

            return (
              <Card key={request.id} id={`request-${request.id}`} className={`bg-white border-slate-200 overflow-hidden transition-shadow ${highlightId === request.id ? 'ring-2 ring-teal-500 shadow-lg' : ''}`} data-testid={`request-card-${request.id}`}>
                <div className="p-5">
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <h3 className="font-semibold text-slate-900 truncate">{request.debtor_name}</h3>
                        <Badge className={
                          request.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          request.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {request.status === 'approved' ? 'Aprobado' : request.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                        </Badge>
                        <Badge variant="outline">
                          {request.request_type === 'certificate' ? 'Certificado' : 'Pago Reportado'}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                        {request.debtor_dni && <span>DNI: {request.debtor_dni}</span>}
                        <span>
                          {new Date(request.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {request.contact_email && (
                          <span className="flex items-center gap-1 text-teal-600">
                            <Mail className="w-3 h-3" />
                            {request.contact_email}
                          </span>
                        )}
                      </div>

                      {/* Actas inline */}
                      {request.request_type === 'payment_report' && request.acta_numbers?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          <span className="text-xs text-slate-500 mr-1">Actas:</span>
                          {request.acta_numbers.map((acta) => (
                            <Badge key={acta} variant="outline" className="text-xs bg-slate-50">
                              {acta}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Quick preview badges */}
                      {request.request_type === 'payment_report' && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {request.proof_file_url && (
                            <Badge variant="secondary" className="text-xs gap-1">
                              <FileImage className="w-3 h-3" />
                              Comprobante
                            </Badge>
                          )}
                          {request.comments && (
                            <Badge variant="secondary" className="text-xs gap-1">
                              <MessageSquare className="w-3 h-3" />
                              Con comentario
                            </Badge>
                          )}
                          {request.ai_analysis && !request.ai_analysis.error && (
                            <Badge variant="secondary" className="text-xs bg-violet-50 text-violet-700">
                              IA Analizado
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {request.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => handleUpdateStatus(request.id, 'approved')} disabled={updating === request.id} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-btn-${request.id}`}>
                            Aprobar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(request.id, 'rejected')} disabled={updating === request.id} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-btn-${request.id}`}>
                            Rechazar
                          </Button>
                        </>
                      )}
                      {hasDetails && (
                        <Button variant="ghost" size="sm" onClick={() => toggleExpand(request.id)} data-testid={`expand-btn-${request.id}`}>
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && hasDetails && (
                  <div className="border-t border-slate-100 bg-slate-50 p-5 space-y-4">
                    {/* Actas */}
                    {request.acta_numbers?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1.5">Actas reportadas</p>
                        <div className="flex flex-wrap gap-2">
                          {request.acta_numbers.map((acta) => (
                            <Badge key={acta} variant="outline" className="bg-white">
                              {acta}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Comments */}
                    {request.comments && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1.5">Comentarios del cliente</p>
                        <p className="text-sm text-slate-700 bg-white rounded-lg p-3 border border-slate-200">
                          {request.comments}
                        </p>
                      </div>
                    )}

                    {/* Proof file */}
                    {request.proof_file_url && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1.5">Comprobante adjunto</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100"
                          onClick={() => window.open(`${BACKEND_URL}${request.proof_file_url}`, '_blank')}
                          data-testid={`proof-link-${request.id}`}
                        >
                          <FileImage className="w-4 h-4 mr-2" />
                          {request.proof_filename || 'Ver comprobante'}
                          <ExternalLink className="w-3.5 h-3.5 ml-2" />
                        </Button>
                      </div>
                    )}

                    {/* AI Analysis */}
                    {request.ai_analysis && !request.ai_analysis.error && !request.ai_analysis.parse_error && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1.5">Analisis IA del comprobante</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {request.ai_analysis.monto && (
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <p className="text-xs text-slate-500">Monto</p>
                              <p className="font-semibold text-slate-900">{request.ai_analysis.monto}</p>
                            </div>
                          )}
                          {request.ai_analysis.fecha && (
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <p className="text-xs text-slate-500">Fecha</p>
                              <p className="font-semibold text-slate-900">{request.ai_analysis.fecha}</p>
                            </div>
                          )}
                          {request.ai_analysis.metodo_pago && (
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <p className="text-xs text-slate-500">Metodo</p>
                              <p className="font-semibold text-slate-900">{request.ai_analysis.metodo_pago}</p>
                            </div>
                          )}
                          {request.ai_analysis.referencia && (
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <p className="text-xs text-slate-500">Referencia</p>
                              <p className="font-semibold text-slate-900">{request.ai_analysis.referencia}</p>
                            </div>
                          )}
                          {request.ai_analysis.entidad && (
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <p className="text-xs text-slate-500">Entidad</p>
                              <p className="font-semibold text-slate-900">{request.ai_analysis.entidad}</p>
                            </div>
                          )}
                          {request.ai_analysis.confianza && (
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <p className="text-xs text-slate-500">Confianza</p>
                              <p className={`font-semibold ${
                                request.ai_analysis.confianza === 'alta' ? 'text-emerald-600' :
                                request.ai_analysis.confianza === 'media' ? 'text-amber-600' : 'text-red-600'
                              }`}>{request.ai_analysis.confianza}</p>
                            </div>
                          )}
                        </div>
                        {request.ai_analysis.notas && (
                          <p className="text-xs text-slate-500 mt-2 italic">{request.ai_analysis.notas}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RequestsPanel;
